import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  productLabelsApi,
  type IssueLabelItem,
  type LabelScan,
  type ListLabelsQuery,
} from '@/api/productLabelsApi'
import { useTenant } from '@/providers/TenantProvider'

/** Printed product labels (z8nrz7dt48), newest first. */
export function useProductLabels(query: ListLabelsQuery, enabled = true) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: ['product-labels', 'list', selectedTenantId, query],
    enabled: enabled && !!selectedTenantId,
    queryFn: () => productLabelsApi.list(query),
  })
}

/** A form's label reconciliation — read before Complete Form, so the check can be synchronous. */
export function useLabelReconciliation(submissionId: string | null | undefined) {
  const { selectedTenantId } = useTenant()
  return useQuery({
    queryKey: ['product-labels', 'summary', selectedTenantId, submissionId],
    enabled: !!submissionId && !!selectedTenantId,
    queryFn: () => productLabelsApi.summary(submissionId as string),
  })
}

/** Every label change moves the lists, the reconciliation and the product's audit trail. */
function useInvalidateLabels() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['product-labels'] })
    void qc.invalidateQueries({ queryKey: ['products', 'created'] })
    void qc.invalidateQueries({ queryKey: ['products', 'audit'] })
  }
}

/** Pre-print verification: record and sign labels; print only after this succeeds. */
export function useIssueLabels() {
  const invalidate = useInvalidateLabels()
  return useMutation({
    mutationFn: (payload: { items: IssueLabelItem[]; signingToken: string; signatureMeaning: string }) =>
      productLabelsApi.issue(payload),
    onSuccess: invalidate,
  })
}

export function useVerifyLabel() {
  const invalidate = useInvalidateLabels()
  return useMutation({
    mutationFn: ({ id, ...payload }: LabelScan & { id: string; signingToken: string; signatureMeaning: string }) =>
      productLabelsApi.verify(id, payload),
    onSuccess: invalidate,
  })
}

export function useSetLabelUsage() {
  const invalidate = useInvalidateLabels()
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string
      usage: 'discarded' | 'unused'
      reason?: string
      signingToken: string
      signatureMeaning: string
    }) => productLabelsApi.setUsage(id, payload),
    onSuccess: invalidate,
  })
}
